import { BaseModalProps, Modal } from '@/components/Modal';
import { useTranslation } from 'react-i18next';
import { TicketForm, TicketFormData, TicketAppointment } from './TicketForm';
import { Ticket, UpdateTicketDto } from '@/lib/api/tickets';
import { parseLocalDate } from '@/lib/utils/parse-local-date';

export interface UpdateTicketModalProps extends BaseModalProps {
  ticket: Ticket;
  onUpdate: (id: string, data: UpdateTicketDto) => Promise<boolean>;
  isUpdating: boolean;
}

export function UpdateTicketModal({
  open,
  onOpenChange,
  ticket,
  onUpdate,
  isUpdating,
}: UpdateTicketModalProps) {
  const { t } = useTranslation();

  const handleSubmit = async (data: TicketFormData & { appointments: TicketAppointment[] }) => {
    const success = await onUpdate(ticket.id, {
      number: data.number,
      title: data.title,
      userId: data.userId,
      date: data.date,
      contractId: data.contractId,
      appointments: data.appointments.map((appointment) => ({
        date: appointment.date.toISOString(),
        workedTime: appointment.workedTime,
        isDoubleDebited: appointment.isDoubleDebited,
        userId: appointment.userId,
      })),
    });

    if (success) {
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={t('tickets.updateModal.title')}>
      <TicketForm
        mode="update"
        onSubmit={handleSubmit}
        isLoading={isUpdating}
        initialData={{
          number: ticket.number,
          title: ticket.title,
          userId: ticket.userId,
          date: parseLocalDate(ticket.date),
          contractId: ticket.contractId,
          appointments:
            ticket.ticketAppointments?.map((appointment) => ({
              id: appointment.id,
              date: parseLocalDate(appointment.date),
              workedTime:
                appointment.workedTime % 60 === 0
                  ? appointment.workedTime / 60
                  : appointment.workedTime,
              timeUnit:
                appointment.workedTime % 60 === 0 ? ('hours' as const) : ('minutes' as const),
              isDoubleDebited: appointment.isDoubleDebited,
              userId: appointment.userId,
            })) || [],
        }}
        onCancel={handleCancel}
      />
    </Modal>
  );
}
